/* eslint-disable max-classes-per-file -- ошибка клиента живёт рядом с ним */
/**
 * HTTP-вызовы ТВ-страницы к зонам API (GEN-19020, решение A3 GEN-19015).
 *
 * Комната создаётся `POST /api/public/v1/rooms`; в ответе — анонимная сессия ТВ (Bearer на сутки,
 * без refresh). Ручки самого ТВ — `/api/tv/v1/…` с этим Bearer: QR и закрытие своей комнаты, логи.
 * Сессия живёт в памяти вкладки рядом с кодом комнаты; истекла (401) или комнаты уже нет (404,
 * например после перезапуска сервера) — `TvRoomGoneError`, страница создаёт комнату заново.
 */

export interface TvRoom {
  code: string;
  accessToken: string;
  expiresAt: string;
}

/** Сессия ТВ истекла или комнаты больше нет: нужна новая комната. */
export class TvRoomGoneError extends Error {
  constructor(readonly status: number) {
    super(`TV room is gone (HTTP ${status})`);
    this.name = 'TvRoomGoneError';
  }
}

type FetchFn = (input: string, init?: RequestInit) => Promise<Response>;

export class TvRoomApi {
  private room: TvRoom | null = null;

  private readonly fetchFn: FetchFn;

  constructor(fetchFn?: FetchFn) {
    this.fetchFn = fetchFn ?? ((input, init) => fetch(input, init));
  }

  /** Текущая комната и её сессия; `null` — комната ещё не создана или сброшена. */
  get current(): TvRoom | null {
    return this.room;
  }

  /** Забыть комнату и сессию (следующий `createRoom` выдаст новые). */
  reset(): void {
    this.room = null;
  }

  private authHeaders(): Record<string, string> {
    if (!this.room) throw new Error('TV session is missing: create a room first');
    return { Authorization: `Bearer ${this.room.accessToken}` };
  }

  /** Создать комнату и получить сессию ТВ. */
  async createRoom(): Promise<TvRoom> {
    const res = await this.fetchFn('/api/public/v1/rooms', { method: 'POST' });
    if (!res.ok) throw new Error(`create room failed: HTTP ${res.status}`);
    const body = (await res.json()) as { code: string; session: { accessToken: string; expiresAt: string } };
    this.room = { code: body.code, accessToken: body.session.accessToken, expiresAt: body.session.expiresAt };
    return this.room;
  }

  /** QR своей комнаты (data URL). `lanQs` — `lanHost=…&lanPort=…` или пусто. */
  async qr(code: string, lanQs: string): Promise<string> {
    const res = await this.fetchFn(`/api/tv/v1/rooms/${encodeURIComponent(code)}/qr${lanQs ? `?${lanQs}` : ''}`, {
      headers: this.authHeaders(),
    });
    if (res.status === 401 || res.status === 404) throw new TvRoomGoneError(res.status);
    if (!res.ok) throw new Error(`qr failed: HTTP ${res.status}`);
    const { qr } = (await res.json()) as { qr: string };
    return qr;
  }

  /** Закрыть свою комнату; ошибки не важны — комната всё равно больше не нужна. Сессия сбрасывается. */
  async closeRoom(code: string): Promise<void> {
    const headers = this.room ? this.authHeaders() : null;
    this.room = null;
    if (!headers) return;
    try {
      await this.fetchFn(`/api/tv/v1/rooms/${encodeURIComponent(code)}/close`, { method: 'POST', headers });
    } catch {
      /* сеть недоступна — комнату закроет отключение сокета ТВ */
    }
  }

  /**
   * Отправить логи консоли ТВ. Возвращает число сохранённых строк. Логи нужнее всего, когда что-то сломалось:
   * комнаты (и сессии) нет или сессия истекла (401) — отправка идёт старым `POST /api/tv/client-logs` без
   * токена. Запасной путь уйдёт вместе со старым.
   */
  async sendClientLogs(payload: Record<string, unknown>): Promise<{ id?: number; saved?: number }> {
    const body = JSON.stringify(payload);
    let res = this.room
      ? await this.fetchFn('/api/tv/v1/client-logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body,
      })
      : null;
    if (!res || res.status === 401) {
      res = await this.fetchFn('/api/tv/client-logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as { id?: number; saved?: number };
  }
}
